import {
  EditOutlined,
  EyeOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Affix, Button, Card, Dropdown, Menu, message, Space, Tag } from "antd";
import ListTable from "common/ListTable";
import { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { GET_SALES_CONTRACT_LIST, SALES_CONTRACT_SEND_MAIL } from "../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../apiServices/common";
import {
  SALES_CONTRACT_CREATE_PERMISSION,
  SALES_CONTRACT_EDIT_PERMISSION,
  SALES_CONTRACT_VIEW_PERMISSION,
} from "../../routes/permissions";
import {
  alignTableColumnValueRight,
  cglFormatDate,
  formatNumberWithCommas,
  hasPermission,
} from "../../utils/functions";
import { isMobileView } from "../Common/AppHelper";
import CustomFilter from "../Common/CustomFilter";
import Header from "../Common/Header";

export default function SalesContact(props) {
  // Props
  const { width } = props;

  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [permitList, setPermitList] = useState();
  const [totalData, setTotalData] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rangeData, setRangeData] = useState(10);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [sendingMail, setSendingMail] = useState(false);

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      // disabled: record.sc_status !== "Created",
    }),
  };

  const handleSendMail = async () => {
    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one created Sales Contract.");
      return;
    }
    setSendingMail(true);
    try {
      const res = await postData(SALES_CONTRACT_SEND_MAIL, {
        ids: selectedRowKeys,
      });
      if (res?.code === 200 || res?.status === "success") {
        message.success(res?.message || "Mail sent successfully.");
        setSelectedRowKeys([]);
      }
    } catch (error) {
      message.error("Failed to send mail.");
    } finally {
      setSendingMail(false);
    }
  };

  const columns = [
    {
      title: "Unique ID",
      dataIndex: "sales_contract_unique_id",
      key: "sales_contract_unique_id",
      width: "25%",
    },
    {
      title: "Contract No.",
      dataIndex: "reference_no",
      key: "reference_no",
    },
    {
      title: "Date",
      dataIndex: "contract_date",
      key: "contract_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Value",
      dataIndex: "contract_value",
      key: "contract_value",
      render: (value) =>
        alignTableColumnValueRight(formatNumberWithCommas(value)),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "SC Status",
      dataIndex: "sc_status",
      key: "sc_status",
      render: (status) => {
        let color = "default";
        if (status === "Created") color = "green";
        else if (status === "Updated") color = "blue";
        else if (status === "Deleted") color = "red";
        return status ? <Tag color={color}>{status}</Tag> : "-";
      },
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => (
        <div className="cursore-pointer">
          <Dropdown overlay={menu(record)}>
            <MoreOutlined style={{ fontSize: "20px" }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllPermitList = useCallback(
    async (passedObject) => {
      const { page: requestedPage, per_page: requestedPerPage, ...requestFilter } = passedObject || {};
      const pageNumber = requestedPage != null ? requestedPage : currentPage;
      const perPage = rangeData ?? requestedPerPage;
      if (requestedPage != null && Number(requestedPage) !== Number(currentPage)) {
        setCurrentPage(Number(requestedPage));
      }
      const customQuery = `${GET_SALES_CONTRACT_LIST}?page=${pageNumber}${perPage != null ? `&per_page=${perPage}` : ""}`;
      const res = await getData(customQuery, false, requestFilter);

      if (res) {
        let masterData = res?.data;
        setPermitList(masterData?.data?.data);
        setTotalData(masterData?.data?.total);
        setRangeData(masterData?.data?.per_page);
      }
      setLoading(false);
    },
    [currentPage, rangeData],
  );

  useEffect(() => {
    setLoading(true);
    setSelectedRowKeys([]);
    getAllPermitList(filterData);
  }, [currentPage, filterData, getAllPermitList]);

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <Link to={`/merchandising/update-sales-contact/${data.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          key: "0",
          disabled: !hasPermission([SALES_CONTRACT_EDIT_PERMISSION]),
        },
        {
          label: (
            <Link to={`/merchandising/view-sales-contact/${data.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          key: "1",
          disabled: !hasPermission([SALES_CONTRACT_VIEW_PERMISSION]),
        },
        {
          label: (
            <Link to={`/merchandising/amend-sales-contact/${data.id}`}>
              <PlusOutlined /> Amend
            </Link>
          ),
          key: "2",
          disabled: !hasPermission([SALES_CONTRACT_EDIT_PERMISSION]),
        },
        {
          label: (
            <Link to={`/merchandising/sales-contact-size-specs/${data.id}`}>
              <PlusOutlined /> Size Spec
            </Link>
          ),
          key: "3",
          disabled: !hasPermission([SALES_CONTRACT_EDIT_PERMISSION]),
        },
      ]}
    />
  );
  const paginate = (page) => {
    setCurrentPage(page);
  };

  const handleFilter = () => {
    setShowFilter((prev) => !prev);
  };

  const extra = (
    <Space>
      <Button
        type="primary"
        size="small"
        icon={<MailOutlined />}
        disabled={
          !hasPermission([SALES_CONTRACT_VIEW_PERMISSION]) ||
          selectedRowKeys.length === 0
        }
        onClick={handleSendMail}
        loading={sendingMail}
      >
        Send Mail{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ""}
      </Button>
      <Button
        type={showFilter ? "danger" : "primary"}
        size="small"
        onClick={() => setShowFilter((prev) => !prev)}
        style={{ float: "right" }}
      >
        {`${showFilter ? "Close" : "Open"} Filter Option`}
      </Button>
      <Button
        key="3"
        size="small"
        type="primary"
        disabled={!hasPermission([SALES_CONTRACT_CREATE_PERMISSION])}
      >
        <Link to="/merchandising/add-sales-contact">
          {hasPermission([SALES_CONTRACT_CREATE_PERMISSION])
            ? "Add New"
            : "No Permission"}
        </Link>
      </Button>
    </Space>
  );

  return (
    <Fragment>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header title="Sales Contract" subTitle="" actions={extra} />
        </Card>
      </Affix>

      <div className="containt-body">
        <Card>
          <CustomFilter
            showFilter={showFilter}
            setShowFilter={setShowFilter}
            tableData={permitList}
            fetchTableData={getAllPermitList}
            filterName="sales_contract"
          />
          <ListTable
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            dataSource={permitList}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              total: totalData,
              pageSize: rangeData,
              onChange: (page) => paginate(page),
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </Card>
      </div>
    </Fragment>
  );
}
